import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { TransformControls } from "three/addons/controls/TransformControls.js";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { CSS2DRenderer, CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { loadGlb, loadRoomPlanGlb, centerModel, collectMeshes } from "./MeshLoader.ts";
import { recolorRoomPlanGlb } from "./RoomPlanLayer.ts";
import { CameraManager } from "./Camera.ts";
import { PanoramaSession } from "./PanoramaSession.ts";
import { HouseEquirectSource } from "./HouseEquirectSource.ts";
import { AnnotationManager } from "../tools/Annotation.ts";
import { NoteAnnotationManager } from "../tools/NoteAnnotation.ts";
import { MeasureTool } from "../tools/MeasureTool.ts";
import { NoteTool } from "../tools/NoteTool.ts";
import { WallSnapTool } from "../tools/WallSnapTool.ts";
import type { WallSnapState } from "../tools/WallSnapTool.ts";
import type { MeasureResult } from "../tools/MeasureTool.ts";
import type { NoteResult } from "../tools/NoteTool.ts";
import { StreetViewMarkers } from "./StreetViewMarkers.ts";
import type {
  HouseFloor, HouseRoomEntry, HouseFloorplanEntry, HousePanoramaEntry,
  HouseMeasurementEntry, HouseNoteEntry, StreetViewManifest,
  MeasurementType, NoteCategory, CameraMode,
} from "../types.ts";
import { renderRoomPlanJson2D, type IcRoomPlanJson } from "../ui/RoomPlanJson2D.ts";

const FLOOR_OFFSET = 1.55;       // standing camera height above floor (m)
const FLOOR_ICON_OFFSET = 0.5;   // icon height above floor (m)
const DEFAULT_ROOM_HEIGHT = 2.7; // fallback cylinder height when raycast misses ceiling

/**
 * Parse the PNG pHYs chunk from an ArrayBuffer.
 * Returns pixels-per-metre if the chunk exists and unit == 1 (metre), else 0.
 *
 * PNG layout: 8-byte signature, then chunks: [4B length][4B type][NB data][4B CRC].
 * pHYs data: [4B ppuX][4B ppuY][1B unit]  — unit 1 = metre.
 */
function _pngPixelsPerMetre(buf: ArrayBuffer): number {
  const v = new DataView(buf);
  // Check PNG signature
  if (v.byteLength < 8) return 0;
  if (v.getUint32(0) !== 0x89504e47 || v.getUint32(4) !== 0x0d0a1a0a) return 0;
  let off = 8;
  while (off + 12 <= v.byteLength) {
    const len = v.getUint32(off);
    const type = v.getUint32(off + 4);
    if (type === 0x70485973 /* pHYs */) {
      if (len >= 9 && off + 8 + len <= v.byteLength) {
        const unit = v.getUint8(off + 8 + 8);
        if (unit === 1) {
          return v.getUint32(off + 8); // pixels per metre (X axis)
        }
      }
      return 0;
    }
    off += 4 + 4 + len + 4; // length + type + data + CRC
  }
  return 0;
}

export type HouseMode = "dollhouse" | "walkthrough" | "plan" | "floorplan" | "walls" | "panorama";

interface FpWall {
  mesh: THREE.Mesh;
  meters: number;
  text: string;
  center: THREE.Vector3;
  selected: boolean;
  label: CSS2DObject | null;
  origMaterial: THREE.Material | THREE.Material[] | null;
}

type HouseSnapshot = {
  rooms: HouseRoomEntry[];
  floorplans: HouseFloorplanEntry[];
  panoramas: HousePanoramaEntry[];
};

export type SelectionType = "room" | "floorplan" | "panorama" | "walls" | "roomFloorplan";
export interface SelectedItem { type: SelectionType; index: number; }

export interface RoomState {
  entry: HouseRoomEntry;
  group: THREE.Group;
  panoramaMarkersGroup: THREE.Group;
  arUcoMarkersGroup: THREE.Group | null;
  loading: boolean;
  progress: number;
  error: string | null;
  centerOffset: THREE.Vector3 | null;          // XZ centering applied by centerModel; null until RTABMap model loads
  wallsGroup: THREE.Group | null;
  wallsLoading: boolean;
  wallsError: string | null;
  floorplanGroup: THREE.Group | null;
  floorplanAutoPosition: THREE.Vector3 | null; // initial auto position of floorplanGroup (for reset)
  floorplanLoading: boolean;
  floorplanError: string | null;
  svMarkers: StreetViewMarkers | null;
  svLoading: boolean;
  svError: string | null;
  svImageBaseUrl: string;
  svImageUrlQuery: string;
}

export interface FloorplanState {
  entry: HouseFloorplanEntry;
  group: THREE.Group;
  loading: boolean;
  error: string | null;
}

export interface PanoramaState {
  entry: HousePanoramaEntry;
  marker: THREE.Group;
  preview: THREE.Mesh | null;
  previewLoading: boolean;
}

export interface HouseComposerOptions {
  /** If set, only these modes appear as buttons in the mode bar. */
  visibleModes?: HouseMode[];
  /** Set to false to hide the blue sphere markers for panorama nodes. Default: true. */
  showPanoramaMarkers?: boolean;
}

export class HouseComposerView {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private orbit: OrbitControls;
  private cameraManager!: CameraManager;
  private transform: TransformControls;
  private _transformMode: "translate" | "rotate" = "translate";
  private animId = 0;
  private _lastFrameTime = 0;
  private readonly _onWalkMouseMove = (e: MouseEvent) => {
    if (document.pointerLockElement === this.renderer.domElement) {
      this.cameraManager.applyPointerDelta(e.movementX, e.movementY);
    }
  };
  private readonly _onKeyDown = (e: KeyboardEvent): void => {
    if (!this._selected) return;
    if (this._mode === "walkthrough") return;
    const t = e.target as HTMLElement;
    if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable) return;

    const isTranslate = this._transformMode === "translate";
    const step    = e.shiftKey ? 0.5  : e.ctrlKey ? 0.005 : 0.05;
    const rotStep = e.shiftKey ? 45   : e.ctrlKey ? 0.1   : 1;

    let dx = 0, dy = 0, dz = 0, drot = 0;
    switch (e.key) {
      case "ArrowLeft":  if (isTranslate) dx = -step; else drot = -rotStep; break;
      case "ArrowRight": if (isTranslate) dx =  step; else drot =  rotStep; break;
      case "ArrowUp":    if (isTranslate) dz = -step; break;
      case "ArrowDown":  if (isTranslate) dz =  step; break;
      case "PageUp":     if (isTranslate) dy =  step; break;
      case "PageDown":   if (isTranslate) dy = -step; break;
      default: return;
    }
    if (dx === 0 && dy === 0 && dz === 0 && drot === 0) return;
    e.preventDefault();
    this._nudgeSelected(dx, dy, dz, drot);
  };
  private rooms: RoomState[] = [];
  private floorplans: FloorplanState[] = [];
  private panoramas: PanoramaState[] = [];
  private _floors: HouseFloor[] = [];
  private _activeFloorId: string | null = null;
  private _selected: SelectedItem | null = null;
  private ro: ResizeObserver;

  private _mode: HouseMode = "dollhouse";
  private _wallsModeActive = false;
  private _undoStack: HouseSnapshot[] = [];
  private _preUndoSnap: HouseSnapshot | null = null;
  private _undoDebounceTimer = 0;
  private _restoringFromUndo = false;
  private _activeTool: "none" | "measure" | "notes" | "wallsnap" = "none";
  private _modeBar!: HTMLElement;
  private _measureSubBar!: HTMLElement;
  private _notesSubBar!: HTMLElement;
  private _wallSnapBar!: HTMLElement;
  private _wallSnap!: WallSnapTool;
  private _panoOverlay!: HTMLElement;
  private _panoSession: PanoramaSession | null = null;
  private _panoSource: HouseEquirectSource | null = null;
  private _blendSlider: HTMLInputElement | null = null;
  private readonly _panoramaMarkersRoot  = new THREE.Group();
  /** Single root for all scene content that should be hidden during panorama. */
  private readonly _sceneRoot = new THREE.Group();
  private readonly _gridGroup = new THREE.Group();

  private labelRenderer: CSS2DRenderer;
  private annotations: AnnotationManager;
  private noteAnnotations: NoteAnnotationManager;
  private measureTool: MeasureTool;
  private noteTool: NoteTool;
  private measurements: HouseMeasurementEntry[] = [];
  private notes_: HouseNoteEntry[] = [];

  private readonly _clipPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 100);
  private _clipActive = false;
  private _clipPct = 85;

  // ── Plan-mode ortho camera & dimensions ────────────────────────────────────
  private _orthoCamera!: THREE.OrthographicCamera;
  private _useOrtho = false;
  private _orthoFrustumH = 20;
  private readonly _planDimGroup = new THREE.Group();
  private _planDimVisible = true;
  private _imperialUnits = true;
  private _fpWalls: FpWall[] = [];
  private _fpHoveredIdx = -1;
  private _fpClickStartX = 0;
  private _fpClickStartY = 0;
  private _planDimBar!: HTMLElement;
  private readonly _roomPlanJsonMap = new Map<string, {
    json: IcRoomPlanJson; centerX: number; centerZ: number;
  }>();
  // Plan-mode pan/zoom state (no OrbitControls — custom handlers)
  private _planDragActive = false;
  private _fpMeasureActive = false;
  private _planHiddenObjects: THREE.Object3D[] = [];
  private _planForcedVisible: THREE.Object3D[] = [];
  private _planDragLastX = 0;
  private _planDragLastY = 0;
  private readonly _onPlanWheel = (e: WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1 / 0.9;
    this._orthoCamera.zoom = Math.max(0.05, Math.min(100, this._orthoCamera.zoom * factor));
    this._orthoCamera.updateProjectionMatrix();
  };
  private readonly _onPlanPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    // Only start drag when the click is directly on the 3D canvas, not on any
    // UI overlay (mode bar, sub-bars, buttons) that share the same container.
    if (e.target !== this.renderer.domElement) return;
    // Let the measure tool handle pointer events when active in floor-plan mode.
    if (this._fpMeasureActive) return;
    this._fpClickStartX = e.clientX;
    this._fpClickStartY = e.clientY;
    this._planDragActive = true;
    this._planDragLastX = e.clientX;
    this._planDragLastY = e.clientY;
    this.renderer.domElement.setPointerCapture(e.pointerId);
  };
  private readonly _onPlanPointerMove = (e: PointerEvent) => {
    if (!this._planDragActive) {
      // Hover: update wall highlight when not dragging (skip when measure tool is active)
      if (!this._fpMeasureActive) this._updateFpHover(e);
      return;
    }
    const dx = e.clientX - this._planDragLastX;
    const dy = e.clientY - this._planDragLastY;
    this._planDragLastX = e.clientX;
    this._planDragLastY = e.clientY;
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    const aspect = w / h;
    // Camera up=(0,0,-1): screen right=world+X, screen down=world-Z (for camera position).
    // To pan, move camera opposite to drag so the scene follows the cursor.
    const scaleX = (this._orthoFrustumH * aspect) / (w * this._orthoCamera.zoom);
    const scaleZ = this._orthoFrustumH / (h * this._orthoCamera.zoom);
    this._orthoCamera.position.x -= dx * scaleX;
    this._orthoCamera.position.z -= dy * scaleZ;
  };
  private readonly _onPlanPointerUp = (e: PointerEvent) => {
    if (!this._planDragActive) return;
    this._planDragActive = false;
    this.renderer.domElement.releasePointerCapture(e.pointerId);
    // Treat as a click if the pointer barely moved — raycast at the click position
    const moved = Math.hypot(e.clientX - this._fpClickStartX, e.clientY - this._fpClickStartY);
    if (moved < 6) this._handleFpClick(this._fpClickStartX, this._fpClickStartY);
  };

  // ArUco markers buffered until the first room GLB is centered (centerOffset becomes known).
  private _pendingArUcoMarkers: Array<{ marker_id: number; world_xyz: [number, number, number] }> = [];
  private _arUcoMarkersRendered = false;
  private _arUcoLabels: CSS2DObject[] = [];
  private _arUcoAnchors: Map<number, THREE.Group> = new Map();

  onArUcoMarkersChange: (() => void) | null = null;
  onSelectionChange: ((sel: SelectedItem | null) => void) | null = null;
  onTransformChange: ((sel: SelectedItem) => void) | null = null;
  onRoomStateChange: ((index: number) => void) | null = null;
  onLoadProgress: ((index: number, pct: number) => void) | null = null;
  onFloorplanStateChange: ((index: number) => void) | null = null;
  onPanoramaStateChange: ((index: number) => void) | null = null;
  /** Fired when the active panorama node changes (index) or exits (null). */
  onActivePanoChange: ((index: number | null) => void) | null = null;
  getPanoramaBlobUrl: ((captureId: string, filename: string) => Promise<string | null>) | null = null;
  /** Called when user clicks a streetview marker in the 3D scene. */
  onStreetViewNodeClick: ((roomIndex: number, nodeId: number) => void) | null = null;
  onModeChange: ((mode: HouseMode) => void) | null = null;
  onUndoStackChange: (() => void) | null = null;
  onMeasureChange: ((measurements: HouseMeasurementEntry[]) => void) | null = null;
  onNoteChange: ((notes: HouseNoteEntry[]) => void) | null = null;
  onFloorChange: (() => void) | null = null;

  constructor(private container: HTMLElement, private readonly _opts: HouseComposerOptions = {}) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.setClearColor(0x0d0f12);
    container.appendChild(this.renderer.domElement);
    this.renderer.domElement.style.cssText = "width:100%;height:100%;display:block;";

    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.domElement.style.cssText =
      "position:absolute;inset:0;pointer-events:none;";
    container.appendChild(this.labelRenderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const dir = new THREE.DirectionalLight(0xffffff, 1.5);
    dir.position.set(5, 10, 5);
    this.scene.add(dir);
    this._gridGroup.add(new THREE.GridHelper(40, 40, 0x333344, 0x222233));

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.01, 500);
    this.camera.position.set(0, 8, 12);
    this.camera.lookAt(0, 0, 0);

    this.orbit = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.1;

    this.cameraManager = new CameraManager(this.camera, this.orbit);
    this.renderer.domElement.addEventListener("click", () => {
      if (this.cameraManager.getMode() === "walkthrough" && !this._panoSession?.isActive) {
        this.renderer.domElement.requestPointerLock();
      }
    });
    document.addEventListener("mousemove", this._onWalkMouseMove);
    document.addEventListener("keydown", this._onKeyDown);

    this.transform = new TransformControls(this.camera, this.renderer.domElement);
    this.transform.setSpace("world");
    this.scene.add(this.transform.getHelper());
    this.setTransformMode("translate");

    this.transform.addEventListener("dragging-changed", (e) => {
      this.orbit.enabled = !(e as any).value;
      if ((e as any).value) {
        this._capturePreSnap();
      } else {
        this._commitPreSnap();
        // Magnetic snap: re-apply after drag if room B hasn't drifted too far
        if (this._wallSnap?.snapActive) {
          const ok = this._wallSnap.enforceSnap(this.rooms);
          if (ok) {
            const idx = this._wallSnap.snapRoomIndex;
            const r = this.rooms[idx];
            if (r) {
              r.group.updateMatrix();
              r.entry.transform = Array.from(r.group.matrix.elements);
              this.onTransformChange?.({ type: "room", index: idx });
            }
          }
        }
      }
    });
    this.transform.addEventListener("objectChange", () => {
      if (!this._selected) return;
      this._syncEntryFromScene(this._selected);
      this.onTransformChange?.(this._selected);
    });

    this.annotations = new AnnotationManager(this.scene);
    this.noteAnnotations = new NoteAnnotationManager(this.scene);

    this.measureTool = new MeasureTool(
      this.renderer.domElement, this.camera, this.annotations,
    );
    this.measureTool.onComplete = (r: MeasureResult) => this._onMeasureComplete(r);
    this.measureTool.onCancel = () => { this._fpMeasureActive = false; this._setActiveTool("none"); };

    this.noteTool = new NoteTool(this.renderer.domElement, this.camera, this.scene);
    this.noteTool.onComplete = (r: NoteResult) => this._onNoteComplete(r);
    this.noteTool.onCancel = () => this._setActiveTool("none");

    this.ro = new ResizeObserver(() => this._resize());
    this.ro.observe(container);
    this._resize();
    this._animate();

    this._sceneRoot.add(this._panoramaMarkersRoot);
    this.scene.add(this._sceneRoot);
    this.scene.add(this._gridGroup);

    // Ortho camera for plan mode
    this._orthoCamera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.01, 1000);
    this._orthoCamera.up.set(0, 0, -1); // -Z up so plan "north" faces up on screen
    this._orthoCamera.position.set(0, 50, 0);
    this._orthoCamera.lookAt(0, 0, 0);
    this._sceneRoot.add(this._planDimGroup);

    this._modeBar = this._createModeBar();
    container.appendChild(this._modeBar);
    this._measureSubBar = this._createMeasureSubBar();
    container.appendChild(this._measureSubBar);
    this._notesSubBar = this._createNotesSubBar();
    container.appendChild(this._notesSubBar);
    this._panoOverlay = this._createPanoOverlay();
    container.appendChild(this._panoOverlay);
    this._planDimBar = this._createPlanDimBar();
    container.appendChild(this._planDimBar);

    this._wallSnap = new WallSnapTool(
      this.renderer.domElement, this.camera, this.scene, () => this.rooms,
    );
    this._wallSnap.onStateChange = (state) => this._updateWallSnapBar(state);
    this._wallSnap.onSnapApplied = (roomIndex) => {
      const r = this.rooms[roomIndex];
      if (!r) return;
      r.group.updateMatrix();
      r.entry.transform = Array.from(r.group.matrix.elements);
      this.onTransformChange?.({ type: "room", index: roomIndex });
    };
    this._wallSnapBar = this._createWallSnapBar();
    container.appendChild(this._wallSnapBar);
  }

  private _resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h);
    this.labelRenderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this._useOrtho && this._orthoCamera) {
      const aspect = w / h;
      const hh = this._orthoFrustumH / 2;
      this._orthoCamera.left = -hh * aspect;
      this._orthoCamera.right = hh * aspect;
      this._orthoCamera.top = hh;
      this._orthoCamera.bottom = -hh;
      this._orthoCamera.updateProjectionMatrix();
    }
  }

  private _animate(time = 0): void {
    this.animId = requestAnimationFrame((t) => this._animate(t));
    const delta = Math.min((time - this._lastFrameTime) / 1000, 0.1);
    this._lastFrameTime = time;
    if (!this._panoSession?.isActive && !this._useOrtho) {
      this.cameraManager.update(delta);
      if (this.cameraManager.getMode() !== "walkthrough") this.orbit.update();
    }
    const activeCam: THREE.Camera = this._useOrtho ? this._orthoCamera : this.camera;
    this.renderer.render(this.scene, activeCam);
    this.labelRenderer.render(this.scene, activeCam);
  }

  private _syncEntryFromScene(sel: SelectedItem): void {
    if (sel.type === "room") {
      const r = this.rooms[sel.index];
      if (!r) return;
      r.group.updateMatrix();
      r.entry.transform = Array.from(r.group.matrix.elements);
    } else if (sel.type === "walls") {
      const r = this.rooms[sel.index];
      if (!r?.wallsGroup) return;
      r.wallsGroup.updateMatrix();
      r.entry.walls_transform = Array.from(r.wallsGroup.matrix.elements);
    } else if (sel.type === "roomFloorplan") {
      const r = this.rooms[sel.index];
      if (!r?.floorplanGroup) return;
      r.floorplanGroup.updateMatrix();
      r.entry.floorplan_transform = Array.from(r.floorplanGroup.matrix.elements);
    } else if (sel.type === "floorplan") {
      const f = this.floorplans[sel.index];
      if (!f) return;
      f.entry.x = f.group.position.x;
      f.entry.y = f.group.position.y;
      f.entry.z = f.group.position.z;
      f.entry.rotation = (f.group.rotation.y * 180) / Math.PI;
    } else if (sel.type === "panorama") {
      const p = this.panoramas[sel.index];
      if (!p) return;
      const pg = this._panoramaParentGroup(p.entry);
      const w = this._markerLocalToWorld(pg, p.marker.position);
      p.entry.x = w.x; p.entry.y = w.y; p.entry.z = w.z;
      p.entry.yaw = (p.marker.rotation.y * 180) / Math.PI;
    }
  }

  setTransformMode(mode: "translate" | "rotate"): void {
    this._transformMode = mode;
    this.transform.setMode(mode);
    // Rotate: Y-axis only (horizontal plane)
    this.transform.showX = mode === "translate";
    this.transform.showY = true;
    this.transform.showZ = mode === "translate";
  }

  setSnap(enabled: boolean, translationM = 0.05, rotationDeg = 1): void {
    this.transform.translationSnap = enabled ? translationM : null;
    this.transform.rotationSnap    = enabled ? (rotationDeg * Math.PI) / 180 : null;
  }

  private _nudgeSelected(dx: number, dy: number, dz: number, drotDeg: number): void {
    const sel = this._selected;
    if (!sel) return;
    let obj: THREE.Object3D | null = null;
    if      (sel.type === "room")          obj = this.rooms[sel.index]?.group        ?? null;
    else if (sel.type === "floorplan")     obj = this.floorplans[sel.index]?.group   ?? null;
    else if (sel.type === "panorama")      obj = this.panoramas[sel.index]?.marker   ?? null;
    else if (sel.type === "walls")         obj = this.rooms[sel.index]?.wallsGroup   ?? null;
    else if (sel.type === "roomFloorplan") obj = this.rooms[sel.index]?.floorplanGroup ?? null;
    if (!obj) return;
    this.pushUndoSnapshot();
    if (dx !== 0)     obj.position.x  += dx;
    if (dy !== 0)     obj.position.y  += dy;
    if (dz !== 0)     obj.position.z  += dz;
    if (drotDeg !== 0) obj.rotation.y += (drotDeg * Math.PI) / 180;
    this._syncEntryFromScene(sel);
    this.onTransformChange?.(sel);
  }

  // ── Section clip (Y-axis only) ─────────────────────────────────────────────

  setClipModeActive(on: boolean): void {
    this._clipActive = on;
    this.renderer.clippingPlanes = on ? [this._clipPlane] : [];
    if (on) this._updateClipPlane();
  }

  get isClipModeActive(): boolean {
    return this._clipActive;
  }

  setSectionHeight(pct: number): void {
    this._clipPct = pct;
    if (this._clipActive) this._updateClipPlane();
  }

  private _updateClipPlane(): void {
    const box = new THREE.Box3();
    for (const room of this.rooms) {
      if (room.entry.visible && !room.loading) box.expandByObject(room.group);
    }
    for (const fp of this.floorplans) {
      if (fp.entry.visible && !fp.loading) box.expandByObject(fp.group);
    }
    const minY = box.isEmpty() ? 0 : box.min.y;
    const maxY = box.isEmpty() ? 4 : box.max.y;
    this._clipPlane.constant = minY + (maxY - minY) * (this._clipPct / 100);
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  selectItem(sel: SelectedItem | null): void {
    this._selected = sel;
    if (sel) {
      let obj: THREE.Object3D | null = null;
      if (sel.type === "room") obj = this.rooms[sel.index]?.group ?? null;
      else if (sel.type === "floorplan") obj = this.floorplans[sel.index]?.group ?? null;
      else if (sel.type === "panorama") obj = this.panoramas[sel.index]?.marker ?? null;
      else if (sel.type === "walls") obj = this.rooms[sel.index]?.wallsGroup ?? null;
      else if (sel.type === "roomFloorplan") obj = this.rooms[sel.index]?.floorplanGroup ?? null;
      if (obj) this.transform.attach(obj);
      else this.transform.detach();
    } else {
      this.transform.detach();
    }
    this.onSelectionChange?.(sel);
  }

  getSelected(): SelectedItem | null { return this._selected; }

  // ── Rooms ──────────────────────────────────────────────────────────────────

  registerRoom(entry: HouseRoomEntry): number {
    const panoramaMarkersGroup = new THREE.Group();
    const state: RoomState = {
      entry: {
        ...entry,
        visible: entry.visible ?? true,
        floor_id: entry.floor_id ?? (this._activeFloorId ?? (this._floors[0]?.id ?? null)),
      },
      group: new THREE.Group(),
      panoramaMarkersGroup,
      arUcoMarkersGroup: null,
      loading: true,
      progress: 0,
      error: null,
      centerOffset: null,
      wallsGroup: null,
      wallsLoading: false,
      wallsError: null,
      floorplanGroup: null,
      floorplanAutoPosition: null,
      floorplanLoading: false,
      floorplanError: null,
      svMarkers: null,
      svLoading: false,
      svError: null,
      svImageBaseUrl: "",
      svImageUrlQuery: "",
    };
    const index = this.rooms.length;
    this.rooms.push(state);
    this._sceneRoot.add(state.group);
    state.group.add(panoramaMarkersGroup);
    state.group.visible = this._roomGroupVisible(index);

    if (entry.transform.length === 16) {
      const m = new THREE.Matrix4();
      m.fromArray(entry.transform);
      state.group.matrix.copy(m);
      state.group.matrix.decompose(
        state.group.position, state.group.quaternion, state.group.scale,
      );
    }
    this.onRoomStateChange?.(index);
    return index;
  }

  async loadRoomModel(index: number, blobUrl: string): Promise<void> {
    const state = this.rooms[index];
    if (!state) return;
    try {
      const loaded = await loadGlb(blobUrl, (pct) => {
        state.progress = pct;
        this.onLoadProgress?.(index, pct);
      });
      centerModel(loaded);
      state.group.add(loaded);
      state.centerOffset = loaded.position.clone();
      state.loading = false;
      this._tryRenderArUcoMarkers();
      this.onRoomStateChange?.(index);
    } catch (err) {
      state.loading = false;
      state.error = (err as Error).message;
      this.onRoomStateChange?.(index);
    }
  }

  addRoom(entry: HouseRoomEntry, blobUrl: string): void {
    const index = this.registerRoom(entry);
    this.loadRoomModel(index, blobUrl);
  }

  removeRoom(index: number): void {
    const state = this.rooms[index];
    if (!state) return;
    if (this._selected?.type === "room" && this._selected.index === index) this.selectItem(null);
    // Reparent any panorama markers that lived inside this room's group.
    for (const p of this.panoramas) {
      if (p.entry.room_capture_id !== state.entry.capture_id) continue;
      state.panoramaMarkersGroup.updateWorldMatrix(true, false);
      const worldPos = state.panoramaMarkersGroup.localToWorld(p.marker.position.clone());
      p.marker.removeFromParent();
      p.marker.position.copy(worldPos);
      this._panoramaMarkersRoot.add(p.marker);
      p.entry.room_capture_id = null;
    }
    if (state.arUcoMarkersGroup) {
      state.arUcoMarkersGroup.traverse((obj) => {
        if (obj instanceof CSS2DObject) obj.element.remove();
      });
      this._arUcoLabels = this._arUcoLabels.filter((l) => l.element.isConnected);
    }
    state.svMarkers?.dispose();
    state.group.removeFromParent();
    this.rooms.splice(index, 1);
    if (this._selected?.type === "room" && (this._selected.index > index)) {
      this._selected = { type: "room", index: this._selected.index - 1 };
    }
  }

  private _roomGroupVisible(index: number): boolean {
    const state = this.rooms[index];
    return state.entry.visible &&
      (this._activeFloorId == null || state.entry.floor_id === this._activeFloorId);
  }

  setRoomVisible(index: number, visible: boolean): void {
    const state = this.rooms[index];
    if (!state) return;
    this._pushUndoImmediate();
    state.entry.visible = visible;
    state.group.visible = this._roomGroupVisible(index);
  }

  // ── Floor management ────────────────────────────────────────────────────────

  getFloors(): HouseFloor[] { return this._floors; }

  getActiveFloorId(): string | null { return this._activeFloorId; }

  setFloors(floors: HouseFloor[]): void {
    this._floors = floors;
    this.onFloorChange?.();
  }

  setActiveFloor(id: string | null): void {
    this._activeFloorId = id;
    for (let i = 0; i < this.rooms.length; i++) {
      this.rooms[i].group.visible = this._roomGroupVisible(i);
    }
    this.onFloorChange?.();
  }

  setRoomFloorId(index: number, floorId: string | null): void {
    const state = this.rooms[index];
    if (!state) return;
    state.entry.floor_id = floorId;
    state.group.visible = this._roomGroupVisible(index);
    this.onFloorChange?.();
  }

  autoDetectFloors(): HouseFloor[] {
    const candidates: { roomIndex: number; floorY: number }[] = [];
    for (let i = 0; i < this.rooms.length; i++) {
      const r = this.rooms[i];
      if (!r.wallsGroup) continue;
      const box = new THREE.Box3().setFromObject(r.group);
      candidates.push({ roomIndex: i, floorY: Math.round(box.min.y * 100) / 100 });
    }
    if (candidates.length === 0) return this._floors;

    candidates.sort((a, b) => a.floorY - b.floorY);

    const clusters: { roomIndices: number[]; minY: number }[] = [];
    for (const c of candidates) {
      const last = clusters[clusters.length - 1];
      if (!last || c.floorY - last.minY > 1.0) {
        clusters.push({ roomIndices: [c.roomIndex], minY: c.floorY });
      } else {
        last.roomIndices.push(c.roomIndex);
      }
    }

    const newFloors: HouseFloor[] = clusters.map((cl, i) => ({
      id: crypto.randomUUID(),
      label: `Floor ${i + 1}`,
      index: i,
      y_offset: cl.minY,
    }));

    for (let fi = 0; fi < clusters.length; fi++) {
      for (const ri of clusters[fi].roomIndices) {
        this.rooms[ri].entry.floor_id = newFloors[fi].id;
      }
    }

    this._floors = newFloors;
    this._activeFloorId = null;
    for (let i = 0; i < this.rooms.length; i++) {
      this.rooms[i].group.visible = this._roomGroupVisible(i);
    }
    this.onFloorChange?.();
    return newFloors;
  }

  setPosition(index: number, x: number, y: number, z: number): void {
    const r = this.rooms[index];
    if (!r) return;
    this.pushUndoSnapshot();
    r.group.position.set(x, y, z);
    r.group.updateMatrix();
    r.entry.transform = Array.from(r.group.matrix.elements);
    this.onTransformChange?.({ type: "room", index });
  }

  setRotationY(index: number, deg: number): void {
    const r = this.rooms[index];
    if (!r) return;
    this.pushUndoSnapshot();
    r.group.rotation.y = (deg * Math.PI) / 180;
    r.group.updateMatrix();
    r.entry.transform = Array.from(r.group.matrix.elements);
    this.onTransformChange?.({ type: "room", index });
  }

  getTransform(index: number): number[] {
    const r = this.rooms[index];
    if (!r) return Array.from(new THREE.Matrix4().elements);
    r.group.updateMatrix();
    return Array.from(r.group.matrix.elements);
  }

  getRooms(): RoomState[] { return this.rooms; }

  // ── Walls (RoomPlan model per room) ───────────────────────────────────────

  async loadRoomWalls(index: number, blobUrl: string): Promise<void> {
    const state = this.rooms[index];
    if (!state) return;
    state.wallsLoading = true;
    state.wallsError = null;
    this.onRoomStateChange?.(index);
    try {
      const loaded = await loadRoomPlanGlb(blobUrl);

      const wallsGroup = new THREE.Group();

      recolorRoomPlanGlb(loaded);
      this._tagWallMeshes(loaded);
      wallsGroup.add(loaded);

      // Restore saved manual-adjustment transform, or apply RTABMap centering offset.
      if (state.entry.walls_transform?.length === 16) {
        const m = new THREE.Matrix4();
        m.fromArray(state.entry.walls_transform);
        wallsGroup.matrix.copy(m);
        wallsGroup.matrix.decompose(wallsGroup.position, wallsGroup.quaternion, wallsGroup.scale);
      } else if (state.centerOffset) {
        // Walls GLB is in RTABMap world XZ space; shift by the same XZ offset
        // that centerModel applied to the RTABMap model so they align.
        // Y is not shifted: walls GLB already has floor at 0 via ty baking.
        wallsGroup.position.set(state.centerOffset.x, 0, state.centerOffset.z);
      }

      wallsGroup.visible = state.entry.walls_visible ?? true;
      state.group.add(wallsGroup);
      state.wallsGroup = wallsGroup;
      state.wallsLoading = false;
      if (this._wallsModeActive) this._applyWallsVisibility();
      this.onRoomStateChange?.(index);
    } catch (err) {
      state.wallsLoading = false;
      state.wallsError = (err as Error).message;
      this.onRoomStateChange?.(index);
    }
  }

  setRoomWallsVisible(index: number, visible: boolean): void {
    const state = this.rooms[index];
    if (!state) return;
    this._pushUndoImmediate();
    state.entry.walls_visible = visible;
    if (state.wallsGroup) state.wallsGroup.visible = visible;
  }

  setRoomWallsOpacity(index: number, opacity: number): void {
    const state = this.rooms[index];
    if (!state?.wallsGroup) return;
    this.pushUndoSnapshot();
    state.entry.walls_opacity = opacity;
    state.wallsGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.material instanceof THREE.MeshBasicMaterial) {
        obj.material.opacity = opacity;
        obj.material.needsUpdate = true;
      }
    });
  }

  resetWallsTransform(index: number): void {
    const state = this.rooms[index];
    if (!state) return;
    this._pushUndoImmediate();
    state.entry.walls_transform = undefined;
    if (state.wallsGroup && state.centerOffset) {
      state.wallsGroup.position.set(state.centerOffset.x, 0, state.centerOffset.z);
      state.wallsGroup.quaternion.identity();
      state.wallsGroup.scale.set(1, 1, 1);
      state.wallsGroup.updateMatrix();
    }
  }

  // ── Room floorplan (SVG overlay per room) ────────────────────────────────

  async loadRoomFloorplan(index: number, svgText: string): Promise<void> {
    const state = this.rooms[index];
    if (!state) return;
    state.floorplanLoading = true;
    state.floorplanError = null;
    this.onRoomStateChange?.(index);

    try {
      // Remove previous floorplan mesh/group if re-loading a different source.
      if (state.floorplanGroup) {
        state.group.remove(state.floorplanGroup);
        state.floorplanGroup.traverse((obj) => {
          if (obj instanceof THREE.Mesh) {
            obj.geometry.dispose();
            if (obj.material instanceof THREE.Material) obj.material.dispose();
          }
        });
        state.floorplanGroup = null;
      }

      // Parse world bounds from SVG data-* attributes (RTABMap XZ space).
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svg = doc.documentElement;
      const minX = parseFloat(svg.getAttribute("data-min-x") ?? "0");
      const maxX = parseFloat(svg.getAttribute("data-max-x") ?? "0");
      const minZ = parseFloat(svg.getAttribute("data-min-y") ?? "0");
      const maxZ = parseFloat(svg.getAttribute("data-max-y") ?? "0");
      const svgW = parseFloat(svg.getAttribute("width") ?? "512");
      const svgH = parseFloat(svg.getAttribute("height") ?? "512");

      const worldWidth = maxX - minX || 5;
      const worldDepth = maxZ - minZ || 5;
      const centerX = (minX + maxX) / 2;
      const centerZ = (minZ + maxZ) / 2;

      const floorplanGroup = new THREE.Group();

      // Restore saved manual-adjustment transform, or place at computed centre
      // shifted by the same XZ offset that centerModel applied to the RTABMap model.
      if (state.entry.floorplan_transform?.length === 16) {
        const m = new THREE.Matrix4();
        m.fromArray(state.entry.floorplan_transform);
        floorplanGroup.matrix.copy(m);
        floorplanGroup.matrix.decompose(
          floorplanGroup.position, floorplanGroup.quaternion, floorplanGroup.scale,
        );
      } else {
        const ox = state.centerOffset?.x ?? 0;
        const oz = state.centerOffset?.z ?? 0;
        floorplanGroup.position.set(centerX + ox, 0.005, centerZ + oz);
        state.floorplanAutoPosition = floorplanGroup.position.clone();
      }

      floorplanGroup.visible = state.entry.floorplan_visible ?? true;
      state.group.add(floorplanGroup);
      state.floorplanGroup = floorplanGroup;

      // Render SVG to canvas for CanvasTexture (vector-quality at any resolution).
      await new Promise<void>((resolve, reject) => {
        const blob = new Blob([svgText], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(svgW, 512);
          canvas.height = Math.max(svgH, 512);
          const ctx = canvas.getContext("2d");
          if (!ctx) { URL.revokeObjectURL(url); reject(new Error("canvas 2d unavailable")); return; }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);

          const texture = new THREE.CanvasTexture(canvas);
          texture.colorSpace = THREE.SRGBColorSpace;

          const geo = new THREE.PlaneGeometry(worldWidth, worldDepth);
          const opacity = state.entry.floorplan_opacity ?? 0.7;
          const mat = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity,
            side: THREE.DoubleSide,
            depthWrite: false,
          });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.rotation.x = -Math.PI / 2; // XZ plane (floor)
          floorplanGroup.add(mesh);
          if (state.entry.floorplan_mirror) {
            floorplanGroup.scale.x = -1;
          }

          state.floorplanLoading = false;
          this.onRoomStateChange?.(index);
          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("SVG image load failed"));
        };
        img.src = url;
      });
    } catch (err) {
      state.floorplanLoading = false;
      state.floorplanError = (err as Error).message;
      this.onRoomStateChange?.(index);
    }
  }

  async loadRoomFloorplanFromJson(index: number, data: IcRoomPlanJson): Promise<void> {
    const state = this.rooms[index];
    if (!state) return;
    const floor = data.floors[0];
    if (!floor?.polygonCorners?.length) return;

    // Compute bounds matching renderRoomPlanJson2D: corners → PAD → SVG extent
    const corners: Array<[number, number]> = floor.polygonCorners.map(([x, y]) => [x, -y]);
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [x, z] of corners) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    }
    const PAD = 0.8;
    minX -= PAD; maxX += PAD; minZ -= PAD; maxZ += PAD;
    const worldW = maxX - minX, worldH = maxZ - minZ;
    const SVG_W = 600, SVG_H = Math.round(SVG_W * worldH / worldW);

    // Store JSON for later dimension generation
    const centerX = (minX + maxX) / 2;
    const centerZ = (minZ + maxZ) / 2;
    this._roomPlanJsonMap.set(state.entry.capture_id, { json: data, centerX, centerZ });

    // Render plan SVG into a detached div, inject data-* bounds expected by loadRoomFloorplan
    const tmpDiv = document.createElement("div");
    renderRoomPlanJson2D(tmpDiv, data);
    const svgEl = tmpDiv.querySelector("svg");
    if (!svgEl) return;
    svgEl.setAttribute("data-min-x", String(minX));
    svgEl.setAttribute("data-max-x", String(maxX));
    svgEl.setAttribute("data-min-y", String(minZ));
    svgEl.setAttribute("data-max-y", String(maxZ));
    svgEl.setAttribute("width",  String(SVG_W));
    svgEl.setAttribute("height", String(SVG_H));

    const svgText = new XMLSerializer().serializeToString(svgEl);
    await this.loadRoomFloorplan(index, svgText);
  }

  setRoomFloorplanVisible(index: number, visible: boolean): void {
    const state = this.rooms[index];
    if (!state) return;
    this._pushUndoImmediate();
    state.entry.floorplan_visible = visible;
    if (state.floorplanGroup) state.floorplanGroup.visible = visible;
  }

  setRoomFloorplanOpacity(index: number, opacity: number): void {
    const state = this.rooms[index];
    if (!state?.floorplanGroup) return;
    this.pushUndoSnapshot();
    state.entry.floorplan_opacity = opacity;
    state.floorplanGroup.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.material instanceof THREE.MeshBasicMaterial) {
        obj.material.opacity = opacity;
        obj.material.needsUpdate = true;
      }
    });
  }

  resetFloorplanTransform(index: number): void {
    const state = this.rooms[index];
    if (!state) return;
    this._pushUndoImmediate();
    state.entry.floorplan_transform = undefined;
    if (state.floorplanGroup && state.floorplanAutoPosition) {
      state.floorplanGroup.position.copy(state.floorplanAutoPosition);
      state.floorplanGroup.quaternion.identity();
      state.floorplanGroup.scale.set(1, 1, 1);
      state.floorplanGroup.updateMatrix();
    }
  }

  toggleRoomFloorplanMirror(index: number): void {
    const state = this.rooms[index];
    if (!state?.floorplanGroup) return;
    this._pushUndoImmediate();
    state.entry.floorplan_mirror = !state.entry.floorplan_mirror;
    state.floorplanGroup.scale.x = state.entry.floorplan_mirror ? -1 : 1;
  }

  // ── Floorplans ────────────────────────────────────────────────────────────

  addFloorplan(entry: HouseFloorplanEntry, blobUrl: string): void {
    const state: FloorplanState = {
      entry: { ...entry, visible: entry.visible ?? true },
      group: new THREE.Group(),
      loading: true,
      error: null,
    };
    const index = this.floorplans.length;
    this.floorplans.push(state);
    this._sceneRoot.add(state.group);
    state.group.position.set(entry.x, entry.y, entry.z);
    state.group.rotation.y = (entry.rotation * Math.PI) / 180;
    state.group.visible = state.entry.visible;
    this.onFloorplanStateChange?.(index);

    // Read real-world scale from PNG pHYs chunk (pixels per metre), then load texture.
    const loadWithScale = async (): Promise<void> => {
      let pxPerM = 0;
      try {
        const buf = await fetch(blobUrl).then(r => r.arrayBuffer());
        pxPerM = _pngPixelsPerMetre(buf);
      } catch { /* ignore, fall through */ }

      new THREE.TextureLoader().load(
        blobUrl,
        (texture) => {
          let w: number, h: number;
          if (pxPerM > 0) {
            w = texture.image.width / pxPerM;
            h = texture.image.height / pxPerM;
          } else {
            // Fallback: 5 m tall, preserve image aspect ratio
            const aspect = texture.image.width / texture.image.height;
            w = 5 * aspect;
            h = 5;
          }
          const geo = new THREE.PlaneGeometry(w, h);
          const mat = new THREE.MeshBasicMaterial({
            map: texture, transparent: true, side: THREE.DoubleSide, opacity: 0.85,
          });
          const mesh = new THREE.Mesh(geo, mat);
          mesh.rotation.x = -Math.PI / 2;
          state.group.add(mesh);
          state.loading = false;
          this.onFloorplanStateChange?.(index);
        },
        undefined,
        (err) => {
          state.loading = false;
          state.error = String(err);
          this.onFloorplanStateChange?.(index);
        },
      );
    };
    void loadWithScale();
  }

  removeFloorplan(index: number): void {
    const state = this.floorplans[index];
    if (!state) return;
    if (this._selected?.type === "floorplan" && this._selected.index === index) this.selectItem(null);
    state.group.removeFromParent();
    this.floorplans.splice(index, 1);
    if (this._selected?.type === "floorplan" && this._selected.index > index) {
      this._selected = { type: "floorplan", index: this._selected.index - 1 };
    }
  }

  setFloorplanPosition(index: number, x: number, y: number, z: number): void {
    const f = this.floorplans[index];
    if (!f) return;
    this.pushUndoSnapshot();
    f.entry.x = x; f.entry.y = y; f.entry.z = z;
    f.group.position.set(x, y, z);
    this.onTransformChange?.({ type: "floorplan", index });
  }

  setFloorplanRotationY(index: number, deg: number): void {
    const f = this.floorplans[index];
    if (!f) return;
    this.pushUndoSnapshot();
    f.entry.rotation = deg;
    f.group.rotation.y = (deg * Math.PI) / 180;
    this.onTransformChange?.({ type: "floorplan", index });
  }

  setFloorplanVisible(index: number, visible: boolean): void {
    const f = this.floorplans[index];
    if (!f) return;
    this._pushUndoImmediate();
    f.entry.visible = visible;
    f.group.visible = visible;
  }

  getFloorplans(): FloorplanState[] { return this.floorplans; }

  // ── Panoramas ─────────────────────────────────────────────────────────────

  private _panoramaParentGroup(entry: HousePanoramaEntry): THREE.Group {
    if (entry.room_capture_id) {
      const room = this.rooms.find(r => r.entry.capture_id === entry.room_capture_id);
      if (room) return room.panoramaMarkersGroup;
    }
    return this._panoramaMarkersRoot;
  }

  private _worldToMarkerLocal(group: THREE.Group, worldX: number, worldY: number, worldZ: number): THREE.Vector3 {
    group.updateWorldMatrix(true, false);
    return group.worldToLocal(new THREE.Vector3(worldX, worldY - FLOOR_OFFSET + FLOOR_ICON_OFFSET, worldZ));
  }

  private _markerLocalToWorld(group: THREE.Group, localPos: THREE.Vector3): { x: number; y: number; z: number } {
    group.updateWorldMatrix(true, false);
    const w = group.localToWorld(localPos.clone());
    return { x: w.x, y: w.y + FLOOR_OFFSET - FLOOR_ICON_OFFSET, z: w.z };
  }

  addPanorama(entry: HousePanoramaEntry): void {
    const marker = new THREE.Group();
    if (this._opts.showPanoramaMarkers !== false) {
      const geo = new THREE.SphereGeometry(0.15, 12, 8);
      const mat = new THREE.MeshPhongMaterial({ color: 0x3B82F6, emissive: 0x1D4ED8, emissiveIntensity: 0.5 });
      marker.add(new THREE.Mesh(geo, mat));
    }
    const parentGroup = this._panoramaParentGroup(entry);
    marker.position.copy(this._worldToMarkerLocal(parentGroup, entry.x, entry.y, entry.z));
    marker.rotation.y = (entry.yaw * Math.PI) / 180;
    marker.visible = entry.visible ?? true;

    const state: PanoramaState = { entry: { ...entry, visible: entry.visible ?? true, mirror: entry.mirror ?? false }, marker, preview: null, previewLoading: false };
    this.panoramas.push(state);
    parentGroup.add(marker);
  }

  removePanorama(index: number): void {
    const state = this.panoramas[index];
    if (!state) return;
    if (this._selected?.type === "panorama" && this._selected.index === index) this.selectItem(null);
    if (state.preview) {
      state.marker.remove(state.preview);
      (state.preview.material as THREE.Material).dispose();
      state.preview.geometry.dispose();
      state.preview = null;
    }
    state.marker.removeFromParent();
    this.panoramas.splice(index, 1);
    if (this._selected?.type === "panorama" && this._selected.index > index) {
      this._selected = { type: "panorama", index: this._selected.index - 1 };
    }
  }

  setPanoramaPosition(index: number, x: number, y: number, z: number): void {
    const p = this.panoramas[index];
    if (!p) return;
    this.pushUndoSnapshot();
    p.entry.x = x; p.entry.y = y; p.entry.z = z;
    const pg = this._panoramaParentGroup(p.entry);
    p.marker.position.copy(this._worldToMarkerLocal(pg, x, y, z));
    this.onTransformChange?.({ type: "panorama", index });
  }

  setPanoramaYaw(index: number, deg: number): void {
    const p = this.panoramas[index];
    if (!p) return;
    this.pushUndoSnapshot();
    p.entry.yaw = deg;
    p.marker.rotation.y = (deg * Math.PI) / 180;
    if (this._panoSession?.isActive && this._panoSession.nodeId === index) {
      this._panoSession.setLiveSphereYaw((deg * Math.PI) / 180 + Math.PI / 2);
    }
    this.onTransformChange?.({ type: "panorama", index });
  }

  setPanoramaVisible(index: number, visible: boolean): void {
    const p = this.panoramas[index];
    if (!p) return;
    this._pushUndoImmediate();
    p.entry.visible = visible;
    p.marker.visible = visible;
  }

  getPanoramas(): PanoramaState[] { return this.panoramas; }

  get panoramaMarkersVisible(): boolean { return this._panoramaMarkersRoot.visible; }

  setPanoramaMarkersVisible(visible: boolean): void {
    this._panoramaMarkersRoot.visible = visible;
  }

  // ── Room streetview ───────────────────────────────────────────────────────

  loadRoomStreetView(
    index: number,
    manifest: StreetViewManifest,
    imageBaseUrl: string,
    imageUrlQuery: string,
  ): void {
    const state = this.rooms[index];
    if (!state) return;
    state.svMarkers?.dispose();
    state.svLoading = false;
    state.svError = null;
    // Markers are children of state.group → positions are group-local.
    // centerOffset is the XZ/Y shift centerModel applied to the GLB within the group,
    // so SLAM(x,y,z) → group-local(x+cx, z+cy, -y+cz) where cx,cy,cz = centerOffset.
    const modelOffset = state.centerOffset?.clone() ?? new THREE.Vector3();
    const markers = new StreetViewMarkers(this.camera, this.renderer, state.group);
    markers.loadManifest(manifest, modelOffset);
    markers.setVisible(state.entry.streetview_visible ?? false);
    markers.onNodeClick = (nodeId) => {
      this.onStreetViewNodeClick?.(index, nodeId);
    };
    state.svMarkers = markers;
    state.svImageBaseUrl = imageBaseUrl;
    state.svImageUrlQuery = imageUrlQuery;
    this.onRoomStateChange?.(index);
  }

  setRoomStreetViewLoading(index: number, loading: boolean, error: string | null = null): void {
    const state = this.rooms[index];
    if (!state) return;
    state.svLoading = loading;
    state.svError = error;
    this.onRoomStateChange?.(index);
  }

  setRoomStreetViewVisible(index: number, visible: boolean): void {
    const state = this.rooms[index];
    if (!state) return;
    state.entry.streetview_visible = visible;
    if (state.svMarkers) state.svMarkers.setVisible(visible);
  }

  setPanoramaPreview(index: number, blobUrl: string | null): void {
    const p = this.panoramas[index];
    if (!p) return;
    if (p.preview) {
      p.marker.remove(p.preview);
      (p.preview.material as THREE.Material).dispose();
      p.preview.geometry.dispose();
      p.preview = null;
    }
    p.previewLoading = false;
    if (!blobUrl) { this.onPanoramaStateChange?.(index); return; }
    p.previewLoading = true;
    this.onPanoramaStateChange?.(index);
    const roomHeight = this._detectRoomHeight(p.entry);
    const geo = new THREE.CylinderGeometry(1, 1, roomHeight, 64, 1, true);
    new THREE.TextureLoader().load(
      blobUrl,
      (texture) => {
        if (p.preview) { geo.dispose(); texture.dispose(); return; } // toggled off while loading
        if (p.entry.mirror) { texture.repeat.set(-1, 1); texture.offset.set(1, 0); }
        const mat = new THREE.MeshBasicMaterial({
          map: texture, side: THREE.BackSide, transparent: true, opacity: 0.6,
        });
        const mesh = new THREE.Mesh(geo, mat);
        // marker is at floorY; shift cylinder up so its bottom sits on the floor
        mesh.position.y = roomHeight / 2 - FLOOR_ICON_OFFSET;
        p.preview = mesh;
        p.previewLoading = false;
        p.marker.add(mesh);
        this.onPanoramaStateChange?.(index);
      },
      undefined,
      () => { geo.dispose(); p.previewLoading = false; this.onPanoramaStateChange?.(index); },
    );
  }

  hasPanoramaPreview(index: number): boolean {
    return !!this.panoramas[index]?.preview;
  }

  setPanoramaPreviewOpacity(index: number, opacity: number): void {
    const p = this.panoramas[index];
    if (!p?.preview) return;
    (p.preview.material as THREE.MeshBasicMaterial).opacity = opacity;
  }

  togglePanoramaPreviewMirror(index: number): void {
    const p = this.panoramas[index];
    if (!p) return;
    p.entry.mirror = !(p.entry.mirror ?? false);
    if (p.preview) {
      const mat = p.preview.material as THREE.MeshBasicMaterial;
      if (mat.map) {
        mat.map.repeat.set(p.entry.mirror ? -1 : 1, 1);
        mat.map.offset.set(p.entry.mirror ? 1 : 0, 0);
        mat.map.needsUpdate = true;
      }
    }
    this.onPanoramaStateChange?.(index);
  }

  // ── House modes ───────────────────────────────────────────────────────────

  getMode(): HouseMode { return this._mode; }

  async setHouseMode(mode: HouseMode): Promise<void> {
    if (this._mode === mode) return;

    if (this._panoSession?.isActive) {
      this._panoSession.exit(); // onExit resets mode to dollhouse
      return;
    }

    const prev = this._mode;
    this._mode = mode;
    this._updateModeBar();

    if (mode === "panorama") {
      if (this.panoramas.length === 0) {
        this._mode = prev;
        this._updateModeBar();
        return;
      }
      let nearest = 0, bestD = Infinity;
      for (let i = 0; i < this.panoramas.length; i++) {
        const d = this.camera.position.distanceTo(
          new THREE.Vector3(this.panoramas[i].entry.x, this.panoramas[i].entry.y, this.panoramas[i].entry.z),
        );
        if (d < bestD) { bestD = d; nearest = i; }
      }
      const ok = await this._startPanoSession(nearest);
      if (!ok) { this._mode = prev; this._updateModeBar(); return; }
    } else {
      this._applyMode(mode);
      this.onModeChange?.(mode);
    }
  }

  async enterPanorama(index: number): Promise<void> {
    if (!this.panoramas[index]) return;
    if (this._panoSession?.isActive) this._panoSession.exit();
    const ok = await this._startPanoSession(index);
    if (ok) { this._mode = "panorama"; this._updateModeBar(); this.onModeChange?.("panorama"); }
  }

  private async _startPanoSession(startIndex: number): Promise<boolean> {
    if (!this.getPanoramaBlobUrl || this.panoramas.length === 0) return false;

    this._gridGroup.visible = false;
    this._panoSession?.dispose();
    this._panoSource?.dispose();

    const count = this.panoramas.length;
    this._panoSource = new HouseEquirectSource(
      this.panoramas.map(p => p.entry),
      this.getPanoramaBlobUrl,
      count - 1, // all others are neighbors — every panorama visible as hotspot
    );

    const planRoot = this._sceneRoot;
    this._panoSession = new PanoramaSession(
      this.camera, this.renderer, this.scene, this.orbit,
      planRoot, this._panoSource, this._panoOverlay,
    );

    this._panoSession.onNodeChange = (nodeId) => {
      this.onActivePanoChange?.(nodeId);
    };
    this._panoSession.onExit = () => {
      this._gridGroup.visible = true;
      if (this._blendSlider) this._blendSlider.value = "0";
      this._mode = "dollhouse";
      this._updateModeBar();
      this._applyMode("dollhouse");
      this.onModeChange?.("dollhouse");
      this.onActivePanoChange?.(null);
    };

    this.selectItem(null);
    await this._panoSession.enter(startIndex);

    // Preload remaining panoramas in the background; track progress in overlay.
    if (count > 1) {
      const preloadEl = this._panoOverlay.querySelector(".pano-preload") as HTMLElement;
      const textEl = this._panoOverlay.querySelector(".pano-preload-text") as HTMLElement;
      const fillEl = this._panoOverlay.querySelector(".pano-preload-fill") as HTMLElement;
      let loaded = 1;
      const update = (): void => {
        if (!this._panoSession?.isActive) { preloadEl.style.display = "none"; return; }
        const pct = Math.round((loaded / count) * 100);
        textEl.textContent = `Caching panoramas ${loaded}/${count}`;
        fillEl.style.width = `${pct}%`;
        preloadEl.style.display = loaded < count ? "" : "none";
      };
      update();
      const src = this._panoSource;
      const tasks = [];
      for (let i = 0; i < count; i++) {
        if (i === startIndex) continue;
        tasks.push(src.preloadNode(i).then(() => { loaded++; update(); }));
      }
      void Promise.all(tasks).then(() => { preloadEl.style.display = "none"; });
    }

    return true;
  }

  private _tagWallMeshes(group: THREE.Group): void {
    group.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const norms = obj.geometry.attributes.normal as THREE.BufferAttribute | undefined;
      if (!norms || norms.count === 0) { obj.userData.isHorizontalSurface = false; return; }
      let sumAbsNy = 0;
      for (let i = 0; i < norms.count; i++) sumAbsNy += Math.abs(norms.getY(i));
      obj.userData.isHorizontalSurface = (sumAbsNy / norms.count) > 0.7;
    });
  }

  private _applyWallsVisibility(): void {
    for (const room of this.rooms) {
      // Keep room.group itself visible so wallsGroup (a child) is reachable.
      // Hide every other child individually.
      room.group.visible = true;
      for (const child of [...room.group.children]) {
        if (child === room.wallsGroup) {
          room.wallsGroup.visible = true;
          room.wallsGroup.traverse((obj) => {
            if (obj instanceof THREE.Mesh) obj.visible = !obj.userData.isHorizontalSurface;
          });
        } else {
          child.visible = false;
        }
      }
    }
    for (const fp of this.floorplans) fp.group.visible = false;
    this._panoramaMarkersRoot.visible = false;
  }

  private _restoreEntryVisibility(): void {
    for (const room of this.rooms) {
      room.group.visible = room.entry.visible;
      for (const child of [...room.group.children]) {
        if (child === room.wallsGroup) {
          room.wallsGroup.visible = room.entry.walls_visible ?? true;
          room.wallsGroup.traverse((obj) => { if (obj instanceof THREE.Mesh) obj.visible = true; });
        } else if (child === room.floorplanGroup) {
          room.floorplanGroup.visible = room.entry.floorplan_visible ?? true;
        } else if (room.svMarkers && child === room.svMarkers.markersGroup) {
          child.visible = room.entry.streetview_visible ?? false;
        } else {
          child.visible = true;
        }
      }
    }
    for (const fp of this.floorplans) fp.group.visible = fp.entry.visible;
    this._panoramaMarkersRoot.visible = true;
  }

  // ── Undo ──────────────────────────────────────────────────────────────────

  private _capturePreSnap(): void {
    if (this._restoringFromUndo || this._preUndoSnap) return;
    const e = this.getEntries();
    this._preUndoSnap = JSON.parse(JSON.stringify({
      rooms: e.rooms, floorplans: e.floorplans, panoramas: e.panoramas,
    }));
  }

  private _commitPreSnap(): void {
    if (!this._preUndoSnap) return;
    this._undoStack.push(this._preUndoSnap);
    if (this._undoStack.length > 20) this._undoStack.shift();
    this._preUndoSnap = null;
    this.onUndoStackChange?.();
  }

  private _pushUndoImmediate(): void {
    if (this._restoringFromUndo) return;
    clearTimeout(this._undoDebounceTimer);
    this._capturePreSnap();
    this._commitPreSnap();
  }

  pushUndoSnapshot(): void {
    if (this._restoringFromUndo) return;
    this._capturePreSnap();
    clearTimeout(this._undoDebounceTimer);
    this._undoDebounceTimer = window.setTimeout(() => this._commitPreSnap(), 600);
  }

  canUndo(): boolean { return this._undoStack.length > 0; }

  undo(): void {
    const snap = this._undoStack.pop();
    if (!snap) return;
    this._restoringFromUndo = true;
    try { this._applySnapshot(snap); }
    finally {
      this._restoringFromUndo = false;
      this.onUndoStackChange?.();
    }
  }

  private _applySnapshot(snap: HouseSnapshot): void {
    const applyMat = (obj: THREE.Object3D, m16: number[]) => {
      const m = new THREE.Matrix4().fromArray(m16);
      obj.matrix.copy(m);
      obj.matrix.decompose(obj.position, obj.quaternion, obj.scale);
    };

    for (let i = 0; i < this.rooms.length; i++) {
      const room = this.rooms[i];
      const e = snap.rooms[i];
      if (!e) continue;
      room.entry = { ...room.entry, ...e };
      if (e.transform?.length === 16) applyMat(room.group, e.transform);
      room.group.visible = this._roomGroupVisible(i);

      if (room.wallsGroup) {
        if (e.walls_transform?.length === 16) applyMat(room.wallsGroup, e.walls_transform);
        room.wallsGroup.visible = e.walls_visible ?? true;
        const wOp = e.walls_opacity ?? 0.35;
        room.wallsGroup.traverse((o) => {
          if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshBasicMaterial) {
            o.material.opacity = wOp; o.material.needsUpdate = true;
          }
        });
      }

      if (room.floorplanGroup) {
        if (e.floorplan_transform?.length === 16) applyMat(room.floorplanGroup, e.floorplan_transform);
        room.floorplanGroup.visible = e.floorplan_visible ?? true;
        const fOp = e.floorplan_opacity ?? 0.7;
        room.floorplanGroup.traverse((o) => {
          if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshBasicMaterial) {
            o.material.opacity = fOp; o.material.needsUpdate = true;
          }
        });
      }
      this.onRoomStateChange?.(i);
    }

    for (let i = 0; i < this.floorplans.length; i++) {
      const fp = this.floorplans[i];
      const e = snap.floorplans[i];
      if (!e) continue;
      fp.entry = { ...e };
      fp.group.position.set(e.x, e.y, e.z);
      fp.group.rotation.y = (e.rotation ?? 0) * Math.PI / 180;
      fp.group.visible = e.visible;
      this.onFloorplanStateChange?.(i);
    }

    for (let i = 0; i < this.panoramas.length; i++) {
      const p = this.panoramas[i];
      const e = snap.panoramas[i];
      if (!e) continue;
      p.entry = { ...e };
      const pg = this._panoramaParentGroup(e);
      p.marker.position.copy(this._worldToMarkerLocal(pg, e.x, e.y, e.z));
      p.marker.rotation.y = (e.yaw ?? 0) * Math.PI / 180;
      p.marker.visible = e.visible ?? true;
    }

    if (this._wallsModeActive) this._applyWallsVisibility();
    if (this._selected) this.onTransformChange?.(this._selected);
  }

  private _applyMode(mode: HouseMode): void {
    // Exit ortho first so _planHiddenObjects restoration doesn't override
    // visibility changes applied below (e.g. walls mode hides the scan mesh).
    if (this._useOrtho && mode !== "floorplan") {
      this._exitPlanOrtho();
    }

    if (this._wallsModeActive && mode !== "walls") {
      this._restoreEntryVisibility();
      this._wallsModeActive = false;
    }
    if (mode === "walls") {
      this._wallsModeActive = true;
      this._applyWallsVisibility();
    }

    if (mode === "floorplan") {
      this._enterPlanOrtho();
      return; // Ortho camera handles everything; skip perspective camera setup
    }

    this.orbit.minPolarAngle = 0;
    this.orbit.maxPolarAngle = Math.PI * 0.85;
    this.orbit.minDistance = 0.01;
    this.orbit.maxDistance = 500;
    this.orbit.enablePan = true;
    this.orbit.enableRotate = true;
    this.orbit.enableZoom = true;
    this.orbit.screenSpacePanning = false;
    this.orbit.update();

    const camMode: CameraMode | null =
      mode === "panorama" ? null :
      mode === "walls" ? "dollhouse" :
      mode === "plan" ? "floorplan" :
      mode as CameraMode; // "dollhouse" | "walkthrough"
    if (camMode) void this.cameraManager.setMode(camMode);
  }

  /** Synchronously exits floor-plan ortho mode before activating a tool that
   *  uses the perspective camera (measure, notes). Switches the logical mode to
   *  dollhouse so the perspective camera and scan meshes are fully restored. */
  private _leaveFpOrthoForTool(): void {
    if (!this._useOrtho) return;
    this._exitPlanOrtho();
    this._mode = "dollhouse";
    this._updateModeBar();
    void this.cameraManager.setMode("dollhouse");
  }

  private _enterPlanOrtho(): void {
    // Compute scene bounds to fit frustum — prefer wallsGroup bounds for accuracy
    const box = new THREE.Box3();
    for (const room of this.rooms) {
      if (!room.entry.visible) continue;
      if (room.wallsGroup) box.expandByObject(room.wallsGroup);
      else if (!room.loading) box.expandByObject(room.group);
    }
    for (const fp of this.floorplans) {
      if (fp.entry.visible && !fp.loading) box.expandByObject(fp.group);
    }
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    if (!box.isEmpty()) { box.getCenter(center); box.getSize(size); }
    else { size.set(10, 3, 10); }

    // Size frustum to fit the horizontal extent
    this._orthoFrustumH = Math.max(size.x, size.z) * 1.3;
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    const aspect = w / h;
    const hh = this._orthoFrustumH / 2;
    this._orthoCamera.left = -hh * aspect;
    this._orthoCamera.right = hh * aspect;
    this._orthoCamera.top = hh;
    this._orthoCamera.bottom = -hh;
    this._orthoCamera.near = 0.01;
    this._orthoCamera.far = 2000;
    this._orthoCamera.zoom = 1;
    // Place camera far above scene centre; up = -Z so plan "north" faces screen top
    this._orthoCamera.position.set(center.x, center.y + size.y + 200, center.z);
    this._orthoCamera.up.set(0, 0, -1);
    this._orthoCamera.lookAt(center.x, center.y, center.z);
    this._orthoCamera.updateProjectionMatrix();

    // Disable regular orbit and attach our custom pan/zoom handlers
    this.orbit.enabled = false;
    this.container.addEventListener("wheel", this._onPlanWheel, { passive: false });
    this.container.addEventListener("pointerdown", this._onPlanPointerDown);
    this.container.addEventListener("pointermove", this._onPlanPointerMove);
    this.container.addEventListener("pointerup", this._onPlanPointerUp);

    this._useOrtho = true;

    // Prepare visibility overrides: hide scan + panorama markers,
    // force-show all wallsGroups, restore everything on exit.
    this._planHiddenObjects = [];
    this._planForcedVisible = [];
    for (const room of this.rooms) {
      if (!room.entry.visible) continue;
      for (const child of room.group.children) {
        if (child === room.arUcoMarkersGroup) continue; // leave as-is

        if (child === room.wallsGroup) {
          // Force walls visible regardless of their current toggle state
          if (!child.visible) {
            child.visible = true;
            this._planForcedVisible.push(child);
          }
          continue;
        }

        // panoramaMarkersGroup, scan mesh, and anything else: hide
        if (child.visible) {
          child.visible = false;
          this._planHiddenObjects.push(child);
        }
      }
    }

    // Interactive wall lines + dimension labels
    this._buildFpWalls();
    this._updatePlanDimBar();
  }

  private _exitPlanOrtho(): void {
    this.orbit.enabled = true;
    this.container.removeEventListener("wheel", this._onPlanWheel);
    this.container.removeEventListener("pointerdown", this._onPlanPointerDown);
    this.container.removeEventListener("pointermove", this._onPlanPointerMove);
    this.container.removeEventListener("pointerup", this._onPlanPointerUp);

    this._useOrtho = false;
    this._planDragActive = false;

    // Restore all visibility overrides
    for (const obj of this._planHiddenObjects) obj.visible = true;
    this._planHiddenObjects = [];
    for (const obj of this._planForcedVisible) obj.visible = false;
    this._planForcedVisible = [];

    this._clearFpWalls();
    this._updatePlanDimBar();
  }

  // ── Plan-mode dimension labels ─────────────────────────────────────────────

  // ── Floor-plan click-to-measure ───────────────────────────────────────────

  private _fmtLen(meters: number): string {
    if (!this._imperialUnits) return meters.toFixed(2) + " m";
    const totalIn = meters * 39.3701;
    const ft = Math.floor(totalIn / 12);
    const inches = Math.round(totalIn % 12);
    return inches === 12 ? `${ft + 1}' 0"` : `${ft}' ${inches}"`;
  }

  private _rebuildFpTexts(): void {
    for (const w of this._fpWalls) {
      w.text = this._fmtLen(w.meters);
      if (w.label) w.label.element.textContent = w.text;
    }
  }

  private _buildFpWalls(): void {
    this._clearFpWalls();
    if (!this._planDimVisible) return;

    if (!document.getElementById("hcv-dim-css")) {
      const s = document.createElement("style");
      s.id = "hcv-dim-css";
      s.textContent = `
        .hcv-dim { background:rgba(10,15,25,.82);color:#d4e8ff;
          font:600 11px/1 system-ui,sans-serif;padding:3px 7px;border-radius:4px;
          pointer-events:none;white-space:nowrap;border:1px solid rgba(100,160,255,.3); }
      `;
      document.head.appendChild(s);
    }

    for (const room of this.rooms) {
      if (!room.entry.visible || !room.wallsGroup) continue;
      room.wallsGroup.updateMatrixWorld(true);

      room.wallsGroup.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);
        if (size.y < 1.5) return;
        const wallLen = Math.max(size.x, size.z);
        if (wallLen < 0.3) return;

        const center = new THREE.Vector3();
        box.getCenter(center);

        this._fpWalls.push({
          mesh: obj,
          meters: wallLen,
          text: this._fmtLen(wallLen),
          center: new THREE.Vector3(center.x, box.max.y + 0.05, center.z),
          selected: false,
          label: null,
          origMaterial: null,
        });
      });
    }
  }

  private _clearFpWalls(): void {
    for (const w of this._fpWalls) {
      if (w.label) { w.label.element.remove(); w.label.removeFromParent(); }
      if (w.origMaterial !== null) this._restoreWallMaterial(w);
    }
    this._fpWalls = [];
    this._fpHoveredIdx = -1;
    this.container.style.cursor = "";
  }

  private _togglePlanDimensions(): void {
    this._planDimVisible = !this._planDimVisible;
    this._planDimGroup.visible = this._planDimVisible;
    if (this._planDimVisible) this._buildFpWalls();
    else this._clearFpWalls();
    this._updatePlanDimBar();
  }

  private _updateFpHover(e: PointerEvent): void {
    if (!this._planDimVisible || this._fpWalls.length === 0) {
      this.container.style.cursor = "";
      return;
    }
    const rect = this.container.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2(ndcX, ndcY), this._orthoCamera);

    const hits = ray.intersectObjects(this._fpWalls.map(w => w.mesh), false);
    const newIdx = hits.length > 0
      ? this._fpWalls.findIndex(w => w.mesh === hits[0].object)
      : -1;

    if (newIdx !== this._fpHoveredIdx) {
      this._fpHoveredIdx = newIdx;
      this.container.style.cursor = newIdx >= 0 ? "pointer" : "";
    }
  }

  private _handleFpClick(clientX: number, clientY: number): void {
    if (!this._planDimVisible || this._fpWalls.length === 0) return;
    const rect = this.container.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2(ndcX, ndcY), this._orthoCamera);

    const hits = ray.intersectObjects(this._fpWalls.map(w => w.mesh), false);
    if (hits.length === 0) return; // click on empty space — keep all selections

    const hitIdx = this._fpWalls.findIndex(w => w.mesh === hits[0].object);
    if (hitIdx < 0) return;

    const wall = this._fpWalls[hitIdx];
    wall.selected = !wall.selected;

    if (wall.selected) {
      // Highlight wall yellow
      this._highlightWallMaterial(wall);
      // Show pinned label
      const div = document.createElement("div");
      div.className = "hcv-dim";
      div.textContent = wall.text;
      const label = new CSS2DObject(div);
      label.position.copy(wall.center);
      this._planDimGroup.add(label);
      wall.label = label;
    } else {
      // Restore original material and remove label
      this._restoreWallMaterial(wall);
      if (wall.label) { wall.label.element.remove(); wall.label.removeFromParent(); wall.label = null; }
    }
  }

  private _highlightWallMaterial(wall: FpWall): void {
    const orig = wall.mesh.material;
    wall.origMaterial = orig;

    const applyYellow = (m: THREE.Material): THREE.Material => {
      const c = m.clone();
      if (c instanceof THREE.MeshStandardMaterial || c instanceof THREE.MeshPhongMaterial) {
        c.emissive.set(0xffd700);
        c.emissiveIntensity = 0.55;
      } else if (c instanceof THREE.MeshBasicMaterial) {
        c.color.set(0xffd700);
      }
      return c;
    };

    wall.mesh.material = Array.isArray(orig)
      ? orig.map(applyYellow)
      : applyYellow(orig);
  }

  private _restoreWallMaterial(wall: FpWall): void {
    if (wall.origMaterial === null) return;
    // Dispose the cloned highlight material(s)
    const cur = wall.mesh.material;
    if (Array.isArray(cur)) cur.forEach(m => m.dispose());
    else (cur as THREE.Material).dispose();
    wall.mesh.material = wall.origMaterial;
    wall.origMaterial = null;
  }

  private _createPlanDimBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "hcv-sub-bar";
    bar.id = "hcv-plan-dim-bar";
    bar.style.display = "none";

    const btn = document.createElement("button");
    btn.className = "hcv-btn active";
    btn.id = "hcv-dim-toggle-btn";
    btn.textContent = "Dimensions";
    btn.onclick = () => this._togglePlanDimensions();
    bar.appendChild(btn);

    const unitBtn = document.createElement("button");
    unitBtn.className = "hcv-btn";
    unitBtn.id = "hcv-unit-toggle-btn";
    unitBtn.textContent = this._imperialUnits ? "ft" : "m";
    unitBtn.onclick = () => {
      this._imperialUnits = !this._imperialUnits;
      unitBtn.textContent = this._imperialUnits ? "ft" : "m";
      this._rebuildFpTexts();
    };
    bar.appendChild(unitBtn);

    return bar;
  }

  private _updatePlanDimBar(): void {
    if (!this._planDimBar) return;
    const inPlan = this._mode === "floorplan";
    this._planDimBar.style.display = inPlan ? "flex" : "none";
    const btn = this._planDimBar.querySelector("#hcv-dim-toggle-btn") as HTMLElement | null;
    if (btn) btn.classList.toggle("active", this._planDimVisible);
    const subBarVisible =
      this._measureSubBar?.style.display === "flex" ||
      this._notesSubBar?.style.display === "flex" ||
      this._wallSnapBar?.style.display === "flex";
    this._planDimBar.style.top = subBarVisible ? "96px" : "";
  }

  private _createWallSnapBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "hcv-sub-bar";
    bar.id = "hcv-wall-snap-bar";
    bar.style.display = "none";

    const hint = document.createElement("span");
    hint.className = "hcv-hint";
    hint.id = "hcv-snap-hint";
    hint.textContent = "Click a wall in room A (anchor)";
    bar.appendChild(hint);

    const sep = this._makeSep();
    bar.appendChild(sep);

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "hcv-btn";
    cancelBtn.textContent = "Cancel";
    cancelBtn.onclick = () => {
      this._wallSnap?.deactivate();
      this._setActiveTool("none");
    };
    bar.appendChild(cancelBtn);

    return bar;
  }

  private _updateWallSnapBar(state: WallSnapState): void {
    if (!this._wallSnapBar) return;
    if (state === "inactive") {
      this._wallSnapBar.style.display = "none";
      return;
    }
    this._wallSnapBar.style.display = "flex";
    const hint = this._wallSnapBar.querySelector("#hcv-snap-hint") as HTMLElement | null;
    if (!hint) return;
    switch (state) {
      case "pickA":
        hint.textContent = "Click a wall in room A (anchor — will not move)";
        hint.style.color = "rgba(0,230,118,.9)";
        break;
      case "pickB":
        hint.textContent = "Click the matching wall in room B (will snap to A)";
        hint.style.color = "rgba(68,138,255,.9)";
        break;
      case "snapped":
        hint.textContent = "Snapped ✓  Drag room B along the wall · drag >20 cm away to release";
        hint.style.color = "rgba(255,255,255,.7)";
        break;
    }
  }

  // ── Measure / Notes tools ─────────────────────────────────────────────────

  startMeasure(type: MeasurementType): void {
    this.noteTool.deactivate();
    this._wallSnap?.deactivate();
    if (this._useOrtho && (type === "distance" || type === "area")) {
      // Stay in floor-plan ortho mode; measure on the Y=0 floor plane.
      this._fpMeasureActive = true;
      this.measureTool.activate(type, [], {
        camera: this._orthoCamera,
        floorPlane: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
      });
    } else {
      this._leaveFpOrthoForTool();
      this.measureTool.activate(type, this._getMeshes());
    }
    this._setActiveTool("measure");
  }

  stopMeasure(): void {
    this.measureTool.deactivate();
    this._fpMeasureActive = false;
    this._setActiveTool("none");
  }

  startNote(category: NoteCategory): void {
    this.measureTool.deactivate();
    this._wallSnap?.deactivate();
    this._leaveFpOrthoForTool();
    this.noteTool.activate(category, this._getMeshes());
    this._setActiveTool("notes");
  }

  stopNote(): void {
    this.noteTool.deactivate();
    this._setActiveTool("none");
  }

  addMeasurementEntry(entry: HouseMeasurementEntry): void {
    this.measurements.push(entry);
    const pts = entry.points.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    this.annotations.addMeasurement(entry.id, entry.type, pts, entry.value, entry.unit, entry.label);
    this.onMeasureChange?.(this.getMeasurements());
  }

  addNoteEntry(entry: HouseNoteEntry): void {
    this.notes_.push(entry);
    const pt = new THREE.Vector3(entry.point[0], entry.point[1], entry.point[2]);
    this.noteAnnotations.addNote(entry.id, entry.category, pt, entry.text);
    this.onNoteChange?.(this.getNotes());
  }

  /**
   * Load ArUco marker positions (RTABMap Z-up world coords) and render them
   * as 3D note labels once a room GLB has loaded and its centerOffset is known.
   * Safe to call before or after rooms load — will retry on each room load.
   */
  addArUcoMarkers(
    markers: Array<{ marker_id: number; world_xyz: [number, number, number] | null }>,
  ): void {
    this._clearArUcoMarkers();
    this._pendingArUcoMarkers = markers.filter(
      (m): m is { marker_id: number; world_xyz: [number, number, number] } => m.world_xyz != null,
    );
    this._arUcoMarkersRendered = false;
    this._tryRenderArUcoMarkers();
  }

  private _clearArUcoMarkers(): void {
    for (const lbl of this._arUcoLabels) lbl.element.remove();
    this._arUcoLabels = [];
    this._arUcoAnchors.clear();
    for (const room of this.rooms) room.arUcoMarkersGroup?.clear();
  }

  private _tryRenderArUcoMarkers(): void {
    if (this._arUcoMarkersRendered || !this._pendingArUcoMarkers.length) return;
    const room = this.rooms.find((r) => r.centerOffset != null);
    if (!room) return;
    this._arUcoMarkersRendered = true;

    // Lazily create a group parented to the room so markers follow any manual transform.
    if (!room.arUcoMarkersGroup) {
      room.arUcoMarkersGroup = new THREE.Group();
      room.group.add(room.arUcoMarkersGroup);
    }
    const parent = room.arUcoMarkersGroup;
    const offset = room.centerOffset!;

    for (const m of this._pendingArUcoMarkers) {
      const [xr, yr, zr] = m.world_xyz;
      // RTABMap Z-up → Three.js Y-up (same as loadGlb rotation.x = -π/2):
      //   x_three = x_rt,  y_three = z_rt,  z_three = -y_rt
      // then shift by centerOffset to get local position inside room.group.
      const localPt = new THREE.Vector3(xr + offset.x, zr + offset.y, -yr + offset.z);

      const anchor = new THREE.Group();
      anchor.position.copy(localPt);
      anchor.visible = false;

      const div = document.createElement("div");
      div.className = "aruco-label";
      div.textContent = `Marker #${m.marker_id}`;
      const label = new CSS2DObject(div);
      label.position.set(0, 0.12, 0);
      anchor.add(label);
      this._arUcoLabels.push(label);
      this._arUcoAnchors.set(m.marker_id, anchor);

      parent.add(anchor);
    }
    this.onArUcoMarkersChange?.();
  }

  setArUcoMarkerVisible(markerId: number, visible: boolean): void {
    const grp = this._arUcoAnchors.get(markerId);
    if (grp) grp.visible = visible;
  }

  setAllArUcoMarkersVisible(visible: boolean): void {
    for (const grp of this._arUcoAnchors.values()) grp.visible = visible;
  }

  getArUcoMarkers(): Array<{ marker_id: number; worldPos: THREE.Vector3 }> {
    return Array.from(this._arUcoAnchors.entries()).map(([id, grp]) => ({
      marker_id: id,
      worldPos: grp.getWorldPosition(new THREE.Vector3()),
    }));
  }

  focusOnArUcoMarker(markerId: number): void {
    const grp = this._arUcoAnchors.get(markerId);
    if (!grp) return;
    const wp = grp.getWorldPosition(new THREE.Vector3());
    this.orbit.target.copy(wp);
    const dir = this.camera.position.clone().sub(wp);
    const dist = dir.length();
    dir.normalize();
    if (dist < 0.5 || dist > 10) {
      dir.set(0.5, 0.7, 1).normalize();
    }
    this.camera.position.copy(wp).addScaledVector(dir, Math.min(Math.max(dist, 1.5), 4));
    this.orbit.update();
  }

  getMeasurements(): HouseMeasurementEntry[] { return [...this.measurements]; }
  getNotes(): HouseNoteEntry[] { return [...this.notes_]; }

  removeMeasurement(id: string): void {
    this.measurements = this.measurements.filter((m) => m.id !== id);
    this.annotations.remove(id);
    this.onMeasureChange?.(this.getMeasurements());
  }

  removeNote(id: string): void {
    this.notes_ = this.notes_.filter((n) => n.id !== id);
    this.noteAnnotations.remove(id);
    this.onNoteChange?.(this.getNotes());
  }

  highlightMeasurement(id: string): void { this.annotations.highlight(id); }
  highlightNote(id: string): void { this.noteAnnotations.highlight(id); }

  setAllMeasurementsVisible(visible: boolean): void { this.annotations.setVisible(visible); }
  setAllNotesVisible(visible: boolean): void { this.noteAnnotations.setVisible(visible); }

  private _getMeshes(): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    for (const r of this.rooms) collectMeshes(r.group).forEach(m => meshes.push(m));
    return meshes;
  }

  private _detectRoomHeight(entry: HousePanoramaEntry): number {
    const meshes = this._getMeshes();
    if (!meshes.length) return DEFAULT_ROOM_HEIGHT;
    const ray = new THREE.Raycaster(
      new THREE.Vector3(entry.x, entry.y, entry.z),
      new THREE.Vector3(0, 1, 0),
    );
    const hits = ray.intersectObjects(meshes, true);
    if (!hits.length) return DEFAULT_ROOM_HEIGHT;
    // ray starts at camera height (entry.y = floorY + FLOOR_OFFSET)
    // ceiling distance from floor = FLOOR_OFFSET + distance to hit
    return Math.max(FLOOR_OFFSET + hits[0].distance, FLOOR_OFFSET + 0.1);
  }

  private _setActiveTool(tool: "none" | "measure" | "notes" | "wallsnap"): void {
    this._activeTool = tool;
    this._syncToolBarStyles();
    if (tool !== "measure")  this._measureSubBar.style.display = "none";
    if (tool !== "notes")    this._notesSubBar.style.display = "none";
    if (tool !== "wallsnap") this._wallSnapBar.style.display = "none";
    this._updatePlanDimBar();
  }

  private _onMeasureComplete(r: MeasureResult): void {
    this._fpMeasureActive = false;
    const entry: HouseMeasurementEntry = {
      id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: r.type,
      points: r.points.map(p => [p.x, p.y, p.z] as [number, number, number]),
      value: r.value,
      unit: r.unit,
      label: null,
    };
    this.measurements.push(entry);
    const pts = r.points;
    this.annotations.addMeasurement(entry.id, entry.type, pts, entry.value, entry.unit, null);
    this.onMeasureChange?.(this.getMeasurements());
  }

  private _onNoteComplete(r: NoteResult): void {
    const entry: HouseNoteEntry = {
      id: `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      category: r.category,
      point: [r.point.x, r.point.y, r.point.z],
      text: r.text,
    };
    this.notes_.push(entry);
    this.noteAnnotations.addNote(entry.id, entry.category, r.point, entry.text);
    this.onNoteChange?.(this.getNotes());
  }

  private _createModeBar(): HTMLElement {
    if (!document.getElementById("hcv-toolbar-css")) {
      const s = document.createElement("style");
      s.id = "hcv-toolbar-css";
      s.textContent = `
        .hcv-bar { position:absolute;top:12px;left:50%;transform:translateX(-50%);z-index:20;
          display:flex;align-items:center;gap:4px;max-width:calc(100vw - 24px);
          background:rgba(15,15,15,.85);backdrop-filter:blur(8px);
          border:1px solid var(--border,rgba(255,255,255,.12));
          border-radius:99px;padding:4px 8px; }
        .hcv-sub-bar { position:absolute;top:52px;left:50%;transform:translateX(-50%);z-index:20;
          display:none;align-items:center;gap:4px;max-width:calc(100vw - 24px);
          background:rgba(15,15,15,.9);backdrop-filter:blur(8px);
          border:1px solid var(--border,rgba(255,255,255,.12));
          border-radius:99px;padding:4px 8px; }
        .hcv-btn { background:none;border:none;border-radius:99px;
          color:rgba(255,255,255,.55);font-size:12px;font-weight:600;cursor:pointer;
          padding:4px 10px;transition:background 80ms,color 80ms;white-space:nowrap; }
        .hcv-btn:hover { color:#fff; }
        .hcv-btn.active { background:rgba(255,255,255,.16);color:#fff; }
        .hcv-sep { width:1px;height:20px;background:var(--border,rgba(255,255,255,.15));margin:0 2px; }
        .hcv-hint { color:rgba(255,255,255,.45);font-size:11px;padding:0 8px;align-self:center;
          flex-shrink:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0; }
      `;
      document.head.appendChild(s);
    }

    const bar = document.createElement("div");
    bar.className = "hcv-bar";

    const allCamModes: Array<{ mode: HouseMode; label: string }> = [
      { mode: "dollhouse", label: "Dollhouse" },
      { mode: "walkthrough", label: "Walk" },
      { mode: "plan", label: "Plan" },
      { mode: "floorplan", label: "Floor plan" },
      { mode: "walls", label: "Walls" },
    ];
    const camModes = this._opts.visibleModes
      ? allCamModes.filter(({ mode }) => this._opts.visibleModes!.includes(mode))
      : allCamModes;
    for (const { mode, label } of camModes) {
      const btn = document.createElement("button");
      btn.className = "hcv-btn";
      btn.dataset.barMode = mode;
      btn.textContent = label;
      btn.onclick = () => { void this.setHouseMode(mode); };
      bar.appendChild(btn);
    }

    bar.appendChild(this._makeSep());

    const measureBtn = document.createElement("button");
    measureBtn.className = "hcv-btn";
    measureBtn.dataset.barTool = "measure";
    measureBtn.textContent = "Measure";
    measureBtn.onclick = () => this._toggleTool("measure");
    bar.appendChild(measureBtn);

    const notesBtn = document.createElement("button");
    notesBtn.className = "hcv-btn";
    notesBtn.dataset.barTool = "notes";
    notesBtn.textContent = "Notes";
    notesBtn.onclick = () => this._toggleTool("notes");
    bar.appendChild(notesBtn);

    const snapBtn = document.createElement("button");
    snapBtn.className = "hcv-btn";
    snapBtn.dataset.barTool = "wallsnap";
    snapBtn.textContent = "Snap";
    snapBtn.title = "Align rooms by clicking matching walls";
    snapBtn.onclick = () => this._toggleWallSnap();
    bar.appendChild(snapBtn);

    if (!this._opts.visibleModes || this._opts.visibleModes.includes("panorama")) {
      bar.appendChild(this._makeSep());

      const panoBtn = document.createElement("button");
      panoBtn.className = "hcv-btn";
      panoBtn.dataset.barMode = "panorama";
      panoBtn.textContent = "Panorama";
      panoBtn.onclick = () => { void this.setHouseMode("panorama"); };
      bar.appendChild(panoBtn);
    }

    this._syncModeBarStyles(bar);
    return bar;
  }

  private _makeSep(): HTMLElement {
    const d = document.createElement("div");
    d.className = "hcv-sep";
    return d;
  }

  private _toggleWallSnap(): void {
    if (this._activeTool === "wallsnap") {
      this._wallSnap?.deactivate();
      this._setActiveTool("none");
    } else {
      this.measureTool.deactivate();
      this.noteTool.deactivate();
      this._wallSnap?.activate();
      this._wallSnapBar.style.display = "flex";
      this._measureSubBar.style.display = "none";
      this._notesSubBar.style.display = "none";
      this._setActiveTool("wallsnap");
    }
  }

  private _toggleTool(tool: "measure" | "notes"): void {
    if (this._activeTool === tool) {
      // second click: close sub-bar + deactivate
      if (tool === "measure") this.stopMeasure();
      else this.stopNote();
      this._syncToolBarStyles();
      return;
    }
    // deactivate whatever is running
    this.measureTool.deactivate();
    this.noteTool.deactivate();
    this._wallSnap?.deactivate();
    this._activeTool = tool;
    if (tool === "measure") {
      this._measureSubBar.style.display = "flex";
      this._notesSubBar.style.display = "none";
    } else {
      this._notesSubBar.style.display = "flex";
      this._measureSubBar.style.display = "none";
    }
    this._syncToolBarStyles();
    this._updatePlanDimBar();
  }

  private _createMeasureSubBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "hcv-sub-bar";

    const types: Array<{ type: MeasurementType; label: string }> = [
      { type: "distance", label: "↔ Distance" },
      { type: "height",   label: "↕ Height" },
      { type: "area",     label: "▭ Area" },
      { type: "point",    label: "• Point" },
    ];
    for (const { type, label } of types) {
      const btn = document.createElement("button");
      btn.className = "hcv-btn";
      btn.dataset.measureType = type;
      btn.textContent = label;
      btn.onclick = () => this.startMeasure(type);
      bar.appendChild(btn);
    }

    const hint = document.createElement("span");
    hint.className = "hcv-hint";
    hint.textContent = "Click on the model to place points · ESC cancel";
    bar.appendChild(hint);
    return bar;
  }

  private _createNotesSubBar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "hcv-sub-bar";

    const cats: Array<{ cat: NoteCategory; svg: string; label: string }> = [
      {
        cat: "room", label: "Room",
        svg: `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
      },
      {
        cat: "note", label: "Note",
        svg: `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
      },
      {
        cat: "damage", label: "Damage",
        svg: `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
      },
    ];
    for (const { cat, svg, label } of cats) {
      const btn = document.createElement("button");
      btn.className = "hcv-btn";
      btn.dataset.noteCategory = cat;
      btn.innerHTML = `${svg} ${label}`;
      btn.style.display = "flex";
      btn.style.alignItems = "center";
      btn.style.gap = "4px";
      btn.onclick = () => this.startNote(cat);
      bar.appendChild(btn);
    }

    const hint = document.createElement("span");
    hint.className = "hcv-hint";
    hint.textContent = "Click on the model to place a note · ESC cancel";
    bar.appendChild(hint);
    return bar;
  }

  private _updateModeBar(): void {
    if (this._modeBar) this._syncModeBarStyles(this._modeBar);
  }

  private _syncModeBarStyles(bar: HTMLElement): void {
    for (const child of bar.children) {
      const btn = child as HTMLElement;
      if (btn.dataset.barMode) {
        const active = btn.dataset.barMode === this._mode;
        btn.classList.toggle("active", active);
      }
    }
  }

  private _syncToolBarStyles(): void {
    for (const child of this._modeBar.children) {
      const btn = child as HTMLElement;
      if (btn.dataset.barTool) {
        btn.classList.toggle("active", btn.dataset.barTool === this._activeTool);
      }
    }
  }

  private _createPanoOverlay(): HTMLElement {
    if (!document.getElementById("house-pano-ov-css")) {
      const s = document.createElement("style");
      s.id = "house-pano-ov-css";
      s.textContent = `
        .house-pano-ov { position:absolute;inset:0;display:none;pointer-events:none; }
        .house-pano-ov.open { display:block; }
        .house-pano-ov .pano-loading {
          position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
          background:rgba(0,0,0,.7);color:#fff;padding:10px 22px;border-radius:8px;
          font-size:13px;display:none;
        }
        .house-pano-ov .pano-info { display:none !important; }
        .house-pano-ov .pano-nav  { display:none !important; }
        .house-pano-ov .house-pano-exit {
          position:absolute;top:10px;right:10px;pointer-events:all;
          background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.18);
          color:#fff;border-radius:6px;padding:5px 12px;cursor:pointer;font-size:12px;
          transition:background 100ms;
        }
        .house-pano-ov .house-pano-exit:hover { background:rgba(255,255,255,.12); }
        .house-pano-ov .pano-preload {
          position:absolute;bottom:60px;left:50%;transform:translateX(-50%);
          background:rgba(0,0,0,.55);color:rgba(255,255,255,.7);
          padding:4px 14px;border-radius:99px;font-size:11px;white-space:nowrap;display:none;
        }
        .house-pano-ov .pano-preload-bar {
          height:2px;background:rgba(255,255,255,.2);border-radius:1px;margin-top:4px;
        }
        .house-pano-ov .pano-preload-fill {
          height:100%;background:rgba(100,180,255,.8);border-radius:1px;
          transition:width 200ms ease;
        }
        .house-pano-ov .pano-blend-wrap {
          position:absolute;bottom:24px;left:50%;transform:translateX(-50%);
          display:flex;align-items:center;gap:8px;pointer-events:all;
          background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.14);
          border-radius:99px;padding:5px 14px;
        }
        .house-pano-ov .pano-blend-label {
          color:rgba(255,255,255,.65);font-size:11px;white-space:nowrap;user-select:none;
        }
        .house-pano-ov .pano-blend-slider {
          width:110px;cursor:pointer;accent-color:#4cc9f0;
        }
      `;
      document.head.appendChild(s);
    }

    const ov = document.createElement("div");
    ov.className = "house-pano-ov";

    const loading = document.createElement("div");
    loading.className = "pano-loading";
    loading.textContent = "Loading…";
    ov.appendChild(loading);

    const info = document.createElement("div");
    info.className = "pano-info";
    ov.appendChild(info);

    const nav = document.createElement("div");
    nav.className = "pano-nav";
    ov.appendChild(nav);

    const preload = document.createElement("div");
    preload.className = "pano-preload";
    const preloadText = document.createElement("span");
    preloadText.className = "pano-preload-text";
    preload.appendChild(preloadText);
    const preloadBar = document.createElement("div");
    preloadBar.className = "pano-preload-bar";
    const preloadFill = document.createElement("div");
    preloadFill.className = "pano-preload-fill";
    preloadFill.style.width = "0%";
    preloadBar.appendChild(preloadFill);
    preload.appendChild(preloadBar);
    ov.appendChild(preload);

    const exitBtn = document.createElement("button");
    exitBtn.className = "house-pano-exit";
    exitBtn.textContent = "✕ Exit";
    exitBtn.onclick = () => this._panoSession?.exit();
    ov.appendChild(exitBtn);

    const blendWrap = document.createElement("div");
    blendWrap.className = "pano-blend-wrap";
    const lblPano = document.createElement("span");
    lblPano.className = "pano-blend-label";
    lblPano.textContent = "360°";
    const slider = document.createElement("input");
    slider.type = "range";
    slider.className = "pano-blend-slider";
    slider.min = "0";
    slider.max = "100";
    slider.value = "0";
    slider.step = "1";
    slider.addEventListener("input", () => {
      this._panoSession?.setMeshBlend(Number(slider.value) / 100);
    });
    const lbl3d = document.createElement("span");
    lbl3d.className = "pano-blend-label";
    lbl3d.textContent = "3D";
    blendWrap.appendChild(lblPano);
    blendWrap.appendChild(slider);
    blendWrap.appendChild(lbl3d);
    ov.appendChild(blendWrap);
    this._blendSlider = slider;

    return ov;
  }

  // ── Composite ─────────────────────────────────────────────────────────────

  getEntries(): {
    rooms: HouseRoomEntry[];
    floorplans: HouseFloorplanEntry[];
    panoramas: HousePanoramaEntry[];
    measurements: HouseMeasurementEntry[];
    notes: HouseNoteEntry[];
    floors: HouseFloor[];
  } {
    return {
      rooms: this.rooms.map((r, i) => {
        const walls_transform = r.wallsGroup
          ? (() => { r.wallsGroup!.updateMatrix(); return Array.from(r.wallsGroup!.matrix.elements); })()
          : r.entry.walls_transform;
        const floorplan_transform = r.floorplanGroup
          ? (() => { r.floorplanGroup!.updateMatrix(); return Array.from(r.floorplanGroup!.matrix.elements); })()
          : r.entry.floorplan_transform;
        return { ...r.entry, transform: this.getTransform(i), walls_transform, floorplan_transform };
      }),
      floorplans: this.floorplans.map((f) => ({ ...f.entry })),
      panoramas: this.panoramas.map((p) => ({ ...p.entry })),
      measurements: [...this.measurements],
      notes: [...this.notes_],
      floors: [...this._floors],
    };
  }

  async exportGlb(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const exporter = new GLTFExporter();
      const exportScene = new THREE.Scene();
      for (const r of this.rooms) exportScene.add(r.group.clone(true));
      for (const f of this.floorplans) exportScene.add(f.group.clone(true));
      exporter.parse(
        exportScene,
        (result) => {
          if (result instanceof ArrayBuffer) resolve(new Blob([result], { type: "model/gltf-binary" }));
          else resolve(new Blob([JSON.stringify(result)], { type: "model/gltf+json" }));
        },
        (err) => reject(err),
        { binary: true },
      );
    });
  }

  dispose(): void {
    cancelAnimationFrame(this.animId);
    document.removeEventListener("mousemove", this._onWalkMouseMove);
    document.removeEventListener("keydown", this._onKeyDown);
    if (document.pointerLockElement === this.renderer.domElement) document.exitPointerLock();
    this._panoSession?.dispose();
    this._panoSource?.dispose();
    this._panoSession = null;
    this._panoSource = null;
    this.measureTool.dispose();
    this.noteTool.dispose();
    this._clearArUcoMarkers();
    this.annotations.clear();
    this.noteAnnotations.dispose();
    this.transform.detach();
    this.transform.dispose();
    this.cameraManager.dispose();
    this.orbit.dispose();
    this.ro.disconnect();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labelRenderer.domElement.remove();
    this._modeBar.remove();
    this._measureSubBar.remove();
    this._notesSubBar.remove();
    this._panoOverlay.remove();
    this._planDimBar?.remove();
    this._wallSnapBar?.remove();
    this._wallSnap?.dispose();
    this._clearFpWalls();
    for (const r of this.rooms) {
      r.svMarkers?.dispose();
      r.floorplanGroup?.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material.dispose();
        }
      });
      r.group.removeFromParent();
    }
    for (const f of this.floorplans) f.group.removeFromParent();
    for (const p of this.panoramas) {
      if (p.preview) {
        (p.preview.material as THREE.Material).dispose();
        p.preview.geometry.dispose();
      }
      p.marker.removeFromParent();
    }
    this.rooms = [];
    this.floorplans = [];
    this.panoramas = [];
  }
}
